import _ from 'underscore';

import {assert} from 'Asserts';
import {SCORE_SORTS, Sort, SortKey} from 'search/SearchConstants';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import {
  COMPACT_TABLE_PADDING_VERTICAL,
  ScrollableTableColumn,
  SortDirection,
  TABLE_BODY_BORDER_SIZE,
  TABLE_PADDING_VERTICAL,
} from 'ui-library/scrollable/ScrollableTableConstants';

interface ToggleState {
  selectedItemIds: Array<string>;
}

export function computeStateForToggledRows({
  state,
  toggledIds,
  isSelected,
  rowSelectionMode,
}: {
  state: ToggleState;
  toggledIds: Array<string>;
  isSelected: boolean;
  rowSelectionMode?: ScrollableTableConstants.RowSelectionMode | null;
}): ToggleState {
  const newSelectedIds = computeSelectedIdsForToggledRows(
    state.selectedItemIds,
    toggledIds,
    isSelected,
    rowSelectionMode
  );
  return {
    selectedItemIds: newSelectedIds,
  };
}
export function computeSelectedIdsForToggledRows(
  oldSelectedIds: Array<string>,
  toggledIds: Array<string>,
  isSelected: boolean,
  rowSelectionMode?: ScrollableTableConstants.RowSelectionMode | null
): Array<string> {
  if (isSelected) {
    if (rowSelectionMode === ScrollableTableConstants.RowSelectionMode.SINGLE) {
      assert(toggledIds.length === 1, 'Can only select one item when single select');
      return toggledIds;
    }
    // eslint-disable-next-line no-restricted-syntax
    const newToggledIds = toggledIds.filter((toggledId) => !_.contains(oldSelectedIds, toggledId));
    return [...oldSelectedIds, ...newToggledIds];
  }
  // eslint-disable-next-line no-restricted-syntax
  return oldSelectedIds.filter((id) => !_.contains(toggledIds, id));
}

export function cycleColumnHeaderSort(currentSorts: Array<Sort>, selectedSortKey: string): Sort {
  // If we're sorting by created or modified at, we want to sort by descending first, then by ascending
  const shouldDefaultToReverse = [
    SortKey.SORT_CREATED_AT_KEY as string,
    SortKey.SORT_MODIFIED_AT_KEY as string,
  ].includes(selectedSortKey);

  const currentSort = currentSorts.find((sort) => sort.key === selectedSortKey);
  if (currentSort) {
    return {key: currentSort.key, reverse: !currentSort.reverse};
  }
  return {key: selectedSortKey, reverse: shouldDefaultToReverse};
}

export function getColumnHeaderSortDirection(
  currentSorts: Array<Sort>,
  sortKey: string
): SortDirection | null {
  const currentSort = currentSorts.find((c) => c.key === sortKey);
  if (!currentSort) {
    return null;
  }
  // For score sorts, such as chemical similarity sorts, default order is descending and reverse is ascending.
  const shouldFlipDirection = SCORE_SORTS.includes(currentSort.key as SortKey);
  if ((currentSort.reverse && !shouldFlipDirection) || (!currentSort.reverse && shouldFlipDirection)) {
    return ScrollableTableConstants.DESCENDING_SORT;
  } else {
    return ScrollableTableConstants.ASCENDING_SORT;
  }
}

export function makeFirstColumnFixed(columns: Array<ScrollableTableColumn>): Array<ScrollableTableColumn> {
  if (columns.length > 0) {
    columns[0] = {...columns[0], isFixed: true};
  }
  return columns;
}

export function getRowHeightBuffer(isCompact: boolean): number {
  const padding = isCompact ? COMPACT_TABLE_PADDING_VERTICAL : TABLE_PADDING_VERTICAL;
  return padding * 2 + TABLE_BODY_BORDER_SIZE;
}
